import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, Link as LinkIcon, ChevronDown, ChevronUp,
  MoreVertical, Archive, Building2, ClipboardCheck, CalendarRange,
} from 'lucide-react';
import { driversApi, projectsApi, tenantAdminApi } from '../api/services';
import type { WeeklyAlignment } from '../api/services';
import type { AnnualStrategicDriver, Project, DriverLifecycleStatus, KpiStatus } from '../types/models';
import { formatProgress, progressCounts } from '../utils/drivers';
import HelpPanel from '../components/HelpPanel';
import { useAuth } from '../auth/AuthProvider';
import { usePrincipal } from '../context/PrincipalContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useToast } from '@/components/ui/toast';
import { useEntitlements } from '../context/EntitlementsContext';
import { usePermissions } from '../context/PermissionsContext';

type DriverFilter = 'all' | 'mine' | 'organisational';

// KPI status comes from the server (one rule on every surface).
const KPI_STATUS_DOT: Record<KpiStatus, string> = {
  Met: 'bg-emerald-500',
  OnTrack: 'bg-emerald-500',
  Behind: 'bg-rose-500',
  NoData: 'bg-muted-foreground/40',
};
const KPI_STATUS_LABEL: Record<KpiStatus, string> = {
  Met: 'Target met',
  OnTrack: 'On track',
  Behind: 'Behind',
  NoData: 'No current value',
};

// ---------------------------------------------------------------------------
// StrategyPage — Annual Strategic Drivers.
//
// One card per driver: title + lifecycle chip + organisational toggle,
// description, progress slider, KPIs, linked items + projects, time-invested
// footer. A lifecycle menu (Draft / Active / Review / Archive…) hangs off
// each card via a Popover; archive triggers a confirmation dialog so the
// reason gets captured. The Add/Edit dialog is a thin form with a chip-style
// KPI list builder.
//
// Sliders use the native HTML range input rather than a Radix Slider shim —
// keeps the migration self-contained and the `accent-primary` Tailwind class
// handles the thumb/fill colour in both light and dark mode.
// ---------------------------------------------------------------------------

function lifecycleClasses(status?: string): { dot: string; chip: string } {
  switch (status) {
    case 'Active':
      return {
        dot: 'bg-emerald-500',
        chip: 'border-emerald-500/60 text-emerald-700 dark:text-emerald-400',
      };
    case 'Review':
      return {
        dot: 'bg-amber-500',
        chip: 'border-amber-500/60 text-amber-700 dark:text-amber-400',
      };
    case 'Draft':
      return {
        dot: 'bg-blue-500',
        chip: 'border-blue-500/60 text-blue-700 dark:text-blue-400',
      };
    default:
      return {
        dot: 'bg-muted-foreground/40',
        chip: 'border-border text-muted-foreground',
      };
  }
}

function ragClasses(rag: string): string {
  switch (rag) {
    case 'Green':
      return 'border-emerald-500/60 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400';
    case 'Amber':
      return 'border-amber-500/60 bg-amber-500/15 text-amber-700 dark:text-amber-400';
    case 'Red':
      return 'border-rose-500/60 bg-rose-500/15 text-rose-700 dark:text-rose-400';
    default:
      return 'border-border bg-transparent text-muted-foreground';
  }
}

export default function StrategyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const { toast } = useToast();
  // Drivers can come without projects: then no projects are listed under them.
  const projectsIncluded = useEntitlements().has('projects');
  const { activePrincipal, isActingOnBehalf } = usePrincipal();
  const isAdmin = user?.isTenantAdmin || user?.role === 'Admin' || user?.role === 'TenantAdmin';
  // Drivers on screen belong to the executive an EA is acting for, so edit
  // rights and the "My Drivers" filter follow that person, not the signed-in EA.
  const ownerId = isActingOnBehalf && activePrincipal ? activePrincipal.id : user?.id;
  // An EA acting on behalf of a TenantAdmin principal can also toggle the org
  // flag on drivers owned by that principal.
  // Every driver and KPI write needs Manage Strategic Drivers (an admin can
  // take it away per user). An EA's own drivers are the exception: they stay
  // writable on the EA default and only an explicit No removes them, which
  // /permissions/me doesn't tell apart, so that case is left to the server.
  const canManageDrivers = usePermissions().can('canManageStrategicDrivers')
    || (user?.role === 'ExecutiveAssistant' && !isActingOnBehalf);
  const canToggleOrgFlag = (driver: AnnualStrategicDriver): boolean => {
    if (!canManageDrivers) return false;
    if (isAdmin) return true;
    if (isActingOnBehalf && activePrincipal?.isTenantAdmin && driver.userId === activePrincipal.id) return true;
    return false;
  };
  const [driverFilter, setDriverFilter] = useState<DriverFilter>('all');
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [loading, setLoading] = useState(true); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDriver, setEditDriver] = useState<Partial<AnnualStrategicDriver> | null>(null);
  const [kpiInput, setKpiInput] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [weekAlignment, setWeekAlignment] = useState<WeeklyAlignment | null>(null);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedDrivers, setArchivedDrivers] = useState<AnnualStrategicDriver[]>([]);

  // Archive dialog
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveDriverId, setArchiveDriverId] = useState<string | null>(null);
  const [archiveReason, setArchiveReason] = useState<string>('Completed');

  // Expanded descriptions
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

  const toggleDescription = (id: string) => {
    setExpandedDescriptions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Financial Year settings
  const [useFinancialYear, setUseFinancialYear] = useState(false);
  const [fyStartMonth, setFyStartMonth] = useState(1);
  const [fyLabel, setFyLabel] = useState('');
  const [timeInvested, setTimeInvested] = useState<Record<string, {
    self: { workItemMinutes: number; meetingMinutes: number };
    team: { workItemMinutes: number; meetingMinutes: number };
    byMember: {
      userId: string;
      name: string;
      isSelf: boolean;
      workItemMinutes: number;
      meetingMinutes: number;
    }[] | null;
  }>>({});
  // Which drivers have had their team breakdown expanded. Time invested shows
  // YOUR time by default — the team split is opt-in per driver, so the card
  // answers "what have I put in" at a glance without turning into a leaderboard.
  const [teamExpanded, setTeamExpanded] = useState<Record<string, boolean>>({});
  const [fyQuarter, setFyQuarter] = useState('');
  const [fyWeek, setFyWeek] = useState(0);

  // Don't offer "Show team" when it would expand to nothing. For admins that
  // means at least one contributor other than you; for everyone else it means
  // the aggregate is non-zero.
  const hasTeamTime = (t: { team: { workItemMinutes: number; meetingMinutes: number };
                            byMember: { isSelf: boolean }[] | null } | undefined) => {
    if (!t) return false;
    if (t.byMember) return t.byMember.some(m => !m.isSelf);
    return t.team.workItemMinutes > 0 || t.team.meetingMinutes > 0;
  };

  const loadDrivers = async () => {
    setLoading(true);
    try {
      setDrivers(await driversApi.getAll());
    } catch (err) {
      // silent-by-design: page load.
      console.error('Failed to load drivers', err);
    } finally {
      setLoading(false);
    }
  };

  const loadArchivedDrivers = async () => {
    try {
      setArchivedDrivers(await driversApi.getArchived());
    } catch (err) {
      // silent-by-design: supplementary "show archived" list.
      console.error('Failed to load archived drivers', err);
    }
  };

  const handleLifecycleChange = async (driverId: string, status: DriverLifecycleStatus, reason?: string) => {
    try {
      await driversApi.updateLifecycle(driverId, { status, reason });
      loadDrivers();
      if (showArchived) loadArchivedDrivers();
    } catch (err: any) {
      console.error('Failed to update lifecycle', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to update driver lifecycle' });
    }
  };

  const handleArchiveConfirm = async () => {
    if (!archiveDriverId) return;
    await handleLifecycleChange(archiveDriverId, 'Archived', archiveReason);
    setArchiveDialogOpen(false);
    setArchiveDriverId(null);
    setArchiveReason('Completed');
  };

  const openArchiveDialog = (driverId: string) => {
    setArchiveDriverId(driverId);
    setArchiveDialogOpen(true);
  };

  useEffect(() => { loadDrivers(); }, []);

  useEffect(() => { if (showArchived) loadArchivedDrivers(); }, [showArchived]);

  // Load YTD time invested once drivers are in, and whenever the set changes.
  useEffect(() => {
    if (drivers.length === 0) return;
    driversApi.getTimeInvested()
      .then(r => setTimeInvested(r.timeInvested))
      .catch(() => {});
  }, [drivers]);

  // Progress and linked counts arrive on each driver from the server (this
  // year's work, every item); alignment is this week's time on driver work.
  useEffect(() => {
    driversApi.getWeeklyAlignment().then(setWeekAlignment).catch(() => setWeekAlignment(null));
  }, []);

  // Completing or re-linking work anywhere changes driver progress.
  useEffect(() => {
    const refresh = () => {
      driversApi.getAll().then(setDrivers).catch(() => {});
      driversApi.getWeeklyAlignment().then(setWeekAlignment).catch(() => {});
    };
    window.addEventListener('cadence:workitem-changed', refresh);
    return () => window.removeEventListener('cadence:workitem-changed', refresh);
  }, []);

  // Load projects for linked display under drivers
  useEffect(() => {
    if (!projectsIncluded) { setAllProjects([]); return; }
    projectsApi.getAll().then(setAllProjects).catch(() => {});
  }, [projectsIncluded]);

  // Load FY settings on mount
  useEffect(() => {
    tenantAdminApi.getFinancialYear().then((fy: any) => {
      setUseFinancialYear(fy.useFinancialYear);
      setFyStartMonth(fy.useFinancialYear ? fy.financialYearStartMonth : 1);
      // Drivers are filed under the year their financial year starts; open on
      // that one (the calendar year hid current drivers from January to the
      // start month for a non-January financial year).
      if (fy.useFinancialYear && fy.financialYearStartMonth > 1) {
        const now = new Date();
        setSelectedYear(now.getMonth() + 1 >= fy.financialYearStartMonth ? now.getFullYear() : now.getFullYear() - 1);
      }
      setFyLabel(fy.currentFYLabel);
      setFyQuarter(`Q${fy.currentQuarter}`);
      setFyWeek(fy.currentFYWeek);
    }).catch(() => {});
  }, []);

  // Format a minute count as "Xh Ym" for the time-invested footer.
  const formatMinutes = (m: number) => {
    const h = Math.floor(m / 60);
    const min = Math.round(m % 60);
    return `${h}h ${min}m`;
  };

  // Year-filtered drivers (exclude archived unless toggled), respecting org filter
  const filteredDrivers = useMemo(
    () => drivers
      .filter(d => d.year === selectedYear && d.lifecycleStatus !== 'Archived')
      .filter(d => {
        if (driverFilter === 'mine') return d.userId === ownerId;
        if (driverFilter === 'organisational') return d.isOrganisational;
        return true; // 'all'
      })
      .sort((a, b) => a.displayOrder - b.displayOrder),
    [drivers, selectedYear, driverFilter, ownerId]
  );

  // Archived filtered by year
  const filteredArchivedDrivers = useMemo(
    () => archivedDrivers.filter(d => d.year === selectedYear),
    [archivedDrivers, selectedYear]
  );

  // Projects grouped by strategic driver
  const projectsByDriver = useMemo(() => {
    const map: Record<string, Project[]> = {};
    allProjects.forEach(p => {
      if (p.strategicDriverId) {
        if (!map[p.strategicDriverId]) map[p.strategicDriverId] = [];
        map[p.strategicDriverId].push(p);
      }
    });
    return map;
  }, [allProjects]);

  const alignmentPercent = weekAlignment?.alignmentPercent == null ? null : Math.round(weekAlignment.alignmentPercent);

  // Available years from data
  const availableYears = useMemo(() => {
    const years = new Set(drivers.map(d => d.year));
    years.add(new Date().getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [drivers]);

  // FY label for a given year
  const getYearLabel = (year: number): string => {
    if (!useFinancialYear || fyStartMonth === 1) return `${year}`;
    const endYear = year + 1;
    return `FY${year % 100}/${endYear % 100}`;
  };

  // The slider sets the owner's own assessment (assessedProgressPercentage).
  const handleProgressUpdate = async (id: string, value: number) => {
    try {
      await driversApi.update(id, { assessedProgressPercentage: value });
      setDrivers(prev => prev.map(d => d.id === id ? { ...d, assessedProgressPercentage: value } : d));
    } catch (err: any) {
      console.error('Failed to update progress', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to update driver progress' });
    }
  };

  const handleOrganisationalToggle = async (driver: AnnualStrategicDriver) => {
    const next = !driver.isOrganisational;
    try {
      await driversApi.update(driver.id, { isOrganisational: next });
      setDrivers(prev => prev.map(d => d.id === driver.id ? { ...d, isOrganisational: next } : d));
    } catch (err: any) {
      console.error('Failed to toggle organisational flag', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to update organisational flag' });
    }
  };

  const handleSave = async () => {
    if (!editDriver?.title) return;
    try {
      // Send only what the dialog edits: the driver object also carries
      // computed figures (progressPercentage is work done this year), and
      // echoing them back would overwrite the exec's own assessment.
      const fields = {
        title: editDriver.title,
        description: editDriver.description,
        displayOrder: editDriver.displayOrder,
        isActive: editDriver.isActive,
        year: editDriver.year || selectedYear,
        kpis: editDriver.kpis,
        notes: editDriver.notes,
        assessedProgressPercentage: editDriver.assessedProgressPercentage,
      };
      if (editDriver.id) {
        await driversApi.update(editDriver.id, fields);
      } else {
        await driversApi.create({ ...fields, isOrganisational: editDriver.isOrganisational });
      }
      setDialogOpen(false);
      setEditDriver(null);
      loadDrivers();
    } catch (err: any) {
      console.error('Failed to save driver', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to save driver' });
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Delete strategic driver?',
      body: 'The driver will be removed from your annual strategy. Work items and projects linked to it stay but lose the connection.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await driversApi.delete(id);
      loadDrivers();
    } catch (err: any) {
      console.error('Failed to delete', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to delete driver' });
    }
  };

  const addKpi = () => {
    if (!kpiInput.trim()) return;
    setEditDriver(prev => ({
      ...prev,
      kpis: [...(prev?.kpis || []), kpiInput.trim()],
    }));
    setKpiInput('');
  };

  const DRIVER_FILTERS: { value: DriverFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'mine', label: 'My Drivers' },
    { value: 'organisational', label: 'Organisational' },
  ];

  return (
    <div className="mx-auto max-w-[1280px] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-light tracking-tight">Annual Strategic Drivers</h1>
          <p className="text-sm text-muted-foreground">Define the 3-4 strategic themes that guide your year</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={String(selectedYear)}
            onValueChange={(v) => setSelectedYear(Number(v))}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableYears.map(y => (
                <SelectItem key={y} value={String(y)}>{getYearLabel(y)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {useFinancialYear && fyLabel && (
            <p className="whitespace-nowrap text-sm text-muted-foreground">
              {fyQuarter} · Week {fyWeek}
            </p>
          )}
          <div
            role="radiogroup"
            aria-label="Driver filter"
            className="inline-flex rounded-md border border-border bg-card/40 p-0.5"
            onKeyDown={(e) => {
              const order = DRIVER_FILTERS.map(f => f.value);
              const i = order.indexOf(driverFilter);
              if (i < 0) return;
              if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                setDriverFilter(order[(i - 1 + order.length) % order.length]);
              } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                setDriverFilter(order[(i + 1) % order.length]);
              }
            }}
          >
            {DRIVER_FILTERS.map(f => {
              const checked = driverFilter === f.value;
              return (
                <Button
                  key={f.value}
                  role="radio"
                  aria-checked={checked}
                  tabIndex={checked ? 0 : -1}
                  size="sm"
                  variant={checked ? 'default' : 'ghost'}
                  onClick={() => setDriverFilter(f.value)}
                  className="h-7 px-3 text-xs"
                >
                  {f.label}
                </Button>
              );
            })}
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={showArchived}
              onCheckedChange={(c) => setShowArchived(!!c)}
              aria-label="Show archived"
            />
            Show archived
          </label>
          {canManageDrivers && (
            <Button
              className="gap-1.5"
              onClick={() => {
                setEditDriver({
                  title: '', description: '',
                  displayOrder: drivers.length + 1, isActive: true,
                  kpis: [], year: selectedYear,
                });
                setDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Add Driver
            </Button>
          )}
        </div>
      </div>

      <HelpPanel title="About Strategic Drivers" storageKey="strategy">
        Strategic drivers define your annual priorities. Link projects and KPIs to track progress.
      </HelpPanel>

      {/* Alignment summary */}
      {alignmentPercent !== null && (
        <Card
          className={cn(
            'mb-5 border-l-[3px]',
            alignmentPercent >= 60
              ? 'border-l-emerald-500'
              : alignmentPercent >= 30
                ? 'border-l-amber-500'
                : 'border-l-rose-500',
          )}
        >
          <CardContent className="py-3">
            <div className="flex items-center gap-3">
              <LinkIcon
                className={cn(
                  'h-4 w-4',
                  alignmentPercent >= 60
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-amber-600 dark:text-amber-400',
                )}
              />
              <p className="text-sm">
                <strong>{alignmentPercent}%</strong> of this week's meeting and work time is on your strategic drivers
              </p>
              <div className="ml-auto h-1.5 max-w-[200px] flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    'h-full transition-all',
                    alignmentPercent >= 60
                      ? 'bg-emerald-500'
                      : alignmentPercent >= 30
                        ? 'bg-amber-500'
                        : 'bg-rose-500',
                  )}
                  style={{ width: `${alignmentPercent}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Driver grid */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {filteredDrivers.map((driver) => {
          const lc = lifecycleClasses(driver.lifecycleStatus);
          // The owner edits, when they're allowed to manage drivers at all.
          const canEdit = driver.userId === ownerId && canManageDrivers;
          const canEditOrg = canToggleOrgFlag(driver);
          const expanded = expandedDescriptions.has(driver.id);
          const linkedCount = driver.progressTotal ?? 0;

          // Left edge bar tone: primary for Active, amber for Review, neutral
          // for Draft / other. Draft cards are also slightly faded.
          const cardBar =
            driver.lifecycleStatus === 'Active'
              ? 'border-l-primary'
              : driver.lifecycleStatus === 'Review'
                ? 'border-l-amber-500'
                : 'border-l-border';

          return (
            <Card
              key={driver.id}
              className={cn(
                'h-full border-l-[3px]',
                cardBar,
                driver.lifecycleStatus === 'Draft' && 'opacity-70',
              )}
            >
              <CardContent>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-base font-semibold">{driver.title}</p>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full border bg-transparent px-2 py-0.5 text-[11px] font-medium',
                          lc.chip,
                        )}
                      >
                        <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', lc.dot)} />
                        {driver.lifecycleStatus || 'Active'}
                      </span>
                      {(driver.isOrganisational || canEditOrg) && (
                        <button
                          type="button"
                          onClick={canEditOrg ? () => handleOrganisationalToggle(driver) : undefined}
                          disabled={!canEditOrg}
                          className={cn(
                            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors',
                            driver.isOrganisational
                              ? 'border-blue-500/60 bg-blue-500/15 text-blue-700 dark:text-blue-300'
                              : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                            !canEditOrg && 'cursor-default',
                          )}
                          aria-pressed={driver.isOrganisational}
                        >
                          <Building2 className="h-3 w-3" />
                          {driver.isOrganisational ? 'Org-wide' : 'Make org-wide'}
                        </button>
                      )}
                    </div>
                    {driver.isOrganisational && driver.userId !== ownerId && driver.ownerName && (
                      <p className="mt-1 text-xs text-muted-foreground">Owner: {driver.ownerName}</p>
                    )}
                    {driver.description && (
                      <div className="mt-1">
                        <button
                          type="button"
                          onClick={() => toggleDescription(driver.id)}
                          className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
                          aria-expanded={expanded}
                        >
                          Description
                          {expanded
                            ? <ChevronUp className="h-3.5 w-3.5" />
                            : <ChevronDown className="h-3.5 w-3.5" />}
                        </button>
                        <div
                          className={cn(
                            'overflow-hidden transition-[max-height] duration-200 ease-out',
                            expanded ? 'max-h-[400px]' : 'max-h-0',
                          )}
                        >
                          <p className="mt-1 text-sm text-muted-foreground">{driver.description}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex items-center">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button size="icon-sm" variant="ghost" aria-label="Lifecycle status">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-44 p-1">
                          {(['Draft', 'Active', 'Review'] as DriverLifecycleStatus[]).map((status) => {
                            const sc = lifecycleClasses(status);
                            return (
                              <button
                                key={status}
                                type="button"
                                onClick={() => handleLifecycleChange(driver.id, status)}
                                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                              >
                                <span aria-hidden className={cn('h-2 w-2 rounded-full', sc.dot)} />
                                Set as {status}
                              </button>
                            );
                          })}
                          <hr className="my-1 border-border" />
                          <button
                            type="button"
                            onClick={() => openArchiveDialog(driver.id)}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted"
                          >
                            <Archive className="h-4 w-4" />
                            Archive…
                          </button>
                        </PopoverContent>
                      </Popover>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        onClick={() => { setEditDriver(driver); setDialogOpen(true); }}
                        aria-label="Edit driver"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        onClick={() => handleDelete(driver.id)}
                        aria-label="Delete driver"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Progress — work done this financial year, from the server (the
                    same figure as the dashboard, Horizon and Reports). Drivers
                    with nothing linked this year say so rather than showing 0%.
                    The slider is the owner's own assessment, kept separately. */}
                <div className="mb-1 mt-3">
                  {linkedCount === 0 ? (
                    <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                      <p className="font-medium">Not started this year</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground/80">
                        No items linked. Tag a work item with this driver to start tracking progress.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="mb-1 flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">Work done this year</p>
                        <p className="text-xs font-semibold tabular-nums">{formatProgress(driver.progressPercentage)}</p>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div className="h-full bg-primary transition-all" style={{ width: `${driver.progressPercentage ?? 0}%` }} />
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground">{progressCounts(driver)}</p>
                    </>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">Your assessment</p>
                    <p className="text-xs font-semibold tabular-nums">{Math.round(driver.assessedProgressPercentage ?? 0)}%</p>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={driver.assessedProgressPercentage ?? 0}
                    onChange={(e) => {
                      if (!canEdit) return;
                      const v = Number(e.target.value);
                      setDrivers(prev => prev.map(d => d.id === driver.id ? { ...d, assessedProgressPercentage: v } : d));
                    }}
                    onMouseUp={(e) => {
                      if (!canEdit) return;
                      handleProgressUpdate(driver.id, Number((e.target as HTMLInputElement).value));
                    }}
                    onTouchEnd={(e) => {
                      if (!canEdit) return;
                      handleProgressUpdate(driver.id, Number((e.target as HTMLInputElement).value));
                    }}
                    disabled={!canEdit}
                    aria-label={`Your assessment of progress for ${driver.title}`}
                    className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary disabled:cursor-not-allowed disabled:opacity-60"
                  />
                </div>

                {/* Structured / plain KPIs */}
                <div className="mt-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">KPIs</p>
                  {driver.structuredKpis && driver.structuredKpis.length > 0 ? (
                    driver.structuredKpis.map((kpi, i) => {
                      const status: KpiStatus = kpi.status ?? 'NoData';
                      return (
                        <div key={i} className="flex items-center gap-2 py-0.5 pl-2">
                          <span
                            role="img"
                            aria-label={KPI_STATUS_LABEL[status]}
                            title={KPI_STATUS_LABEL[status]}
                            className={cn('h-2 w-2 shrink-0 rounded-full', KPI_STATUS_DOT[status])}
                          />
                          <p className="min-w-0 flex-1 truncate text-sm text-muted-foreground">{kpi.name}</p>
                          <p className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
                            {kpi.currentValue ?? '—'}{kpi.unit ? ` ${kpi.unit}` : ''} / {kpi.targetValue ?? '—'}{kpi.unit ? ` ${kpi.unit}` : ''}
                          </p>
                        </div>
                      );
                    })
                  ) : driver.kpis && driver.kpis.length > 0 ? (
                    driver.kpis.map((kpi: string, i: number) => (
                      <p key={i} className="pl-2 text-sm text-muted-foreground">• {kpi}</p>
                    ))
                  ) : (
                    <p className="mt-1 pl-2 text-sm italic text-muted-foreground/60">
                      No KPIs defined — add KPIs to track progress
                    </p>
                  )}
                </div>

                {/* Meta chips: display order, year, linked count */}
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    #{driver.displayOrder}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {getYearLabel(driver.year)}
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border bg-transparent px-2 py-0.5 text-[11px] font-medium',
                      linkedCount > 0
                        ? 'border-blue-500/60 text-blue-700 dark:text-blue-300'
                        : 'border-border text-muted-foreground',
                    )}
                  >
                    <LinkIcon className="h-3 w-3" />
                    {linkedCount} item{linkedCount !== 1 ? 's' : ''} linked this year
                  </span>
                </div>

                {/* Linked projects */}
                <div className="mt-4">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Projects</p>
                  {projectsByDriver[driver.id] && projectsByDriver[driver.id].length > 0 ? (
                    <div className="flex flex-col gap-1">
                      {projectsByDriver[driver.id].map(project => (
                        <button
                          key={project.id}
                          type="button"
                          onClick={() => navigate(`/projects/${project.id}`)}
                          className="flex items-center gap-2 rounded-md px-2 py-1 text-left transition-colors hover:bg-muted/40"
                        >
                          <p className="min-w-0 flex-1 truncate text-sm font-medium">{project.title}</p>
                          <span
                            className={cn(
                              'inline-flex min-w-[50px] items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                              ragClasses(project.ragStatus),
                            )}
                          >
                            {project.ragStatus}
                          </span>
                          <span className="min-w-[32px] text-right text-[11px] text-muted-foreground tabular-nums">
                            {project.progressPercentage}%
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="pl-2 text-sm italic text-muted-foreground/60">No projects linked</p>
                  )}
                </div>

                {/* Time invested */}
                {timeInvested[driver.id] && (
                  <div className="mt-4 border-t border-border pt-3">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Time invested ({fyLabel || 'YTD'})
                    </p>
                    <div className="flex flex-col gap-1">
                      {driver.isOrganisational ? (
                        <>
                          <TimeInvestedRow label="Self" data={timeInvested[driver.id].self} formatMinutes={formatMinutes} />

                          {/* Team is opt-in. Expanded, tenant admins get the
                              named per-contributor split — it answers "who is
                              actually carrying this priority" — while everyone
                              else gets the anonymous aggregate. byMember is
                              null when not permitted, [] when nobody has
                              logged time. */}
                          {hasTeamTime(timeInvested[driver.id]) && (
                            <>
                              <button
                                type="button"
                                onClick={() => setTeamExpanded(prev => ({ ...prev, [driver.id]: !prev[driver.id] }))}
                                aria-expanded={!!teamExpanded[driver.id]}
                                className="mt-1 flex items-center gap-1 self-start text-xs font-semibold text-muted-foreground hover:text-foreground"
                              >
                                {teamExpanded[driver.id]
                                  ? <ChevronUp className="h-3.5 w-3.5" />
                                  : <ChevronDown className="h-3.5 w-3.5" />}
                                {teamExpanded[driver.id] ? 'Hide team' : 'Show team'}
                              </button>

                              {teamExpanded[driver.id] && (
                                timeInvested[driver.id].byMember
                                  ? timeInvested[driver.id].byMember!
                                      .filter(m => !m.isSelf)
                                      .map(m => (
                                        <TimeInvestedRow
                                          key={m.userId}
                                          label={m.name}
                                          data={m}
                                          formatMinutes={formatMinutes}
                                        />
                                      ))
                                  : <TimeInvestedRow label="Team" data={timeInvested[driver.id].team} formatMinutes={formatMinutes} />
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <TimeInvestedRow label={null} data={timeInvested[driver.id].self} formatMinutes={formatMinutes} />
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Archive reason dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={(o) => !o && setArchiveDialogOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Archive Strategic Driver</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Why is this driver being archived?</p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="archive-reason">Reason</Label>
            <Select value={archiveReason} onValueChange={setArchiveReason}>
              <SelectTrigger id="archive-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Completed">Completed — objectives achieved</SelectItem>
                <SelectItem value="Superseded">Superseded — replaced by another driver</SelectItem>
                <SelectItem value="Abandoned">Abandoned — no longer relevant</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setArchiveDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleArchiveConfirm}
              className="gap-1.5 bg-amber-600 text-white hover:bg-amber-700"
            >
              <Archive className="h-4 w-4" />
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archived drivers section */}
      {showArchived && filteredArchivedDrivers.length > 0 && (
        <div className="mt-6">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-muted-foreground">
            <Archive className="h-5 w-5" />
            Archived Drivers ({filteredArchivedDrivers.length})
          </h2>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {filteredArchivedDrivers.map((driver) => (
              <Card key={driver.id} className="h-full border-l-[3px] border-l-border opacity-60">
                <CardContent>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-base font-semibold">{driver.title}</p>
                      <div className="mt-1 flex flex-wrap gap-2">
                        <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                          Archived
                        </span>
                        {driver.archiveReason && (
                          <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                            {driver.archiveReason}
                          </span>
                        )}
                      </div>
                      {driver.archivedAt && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Archived {new Date(driver.archivedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    {canManageDrivers && (
                      <Button size="sm" variant="ghost" onClick={() => handleLifecycleChange(driver.id, 'Active')}>
                        Restore
                      </Button>
                    )}
                  </div>
                  <div className="mt-3">
                    <div className="mb-1 flex justify-between">
                      <p className="text-xs text-muted-foreground">Final Progress</p>
                      <p className="text-xs font-semibold tabular-nums">{formatProgress(driver.progressPercentage)}</p>
                    </div>
                    <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                      <div className="h-full bg-muted-foreground/40" style={{ width: `${driver.progressPercentage ?? 0}%` }} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Add / edit driver dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !o && setDialogOpen(false)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editDriver?.id ? 'Edit Strategic Driver' : 'New Strategic Driver'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="drv-title">Title</Label>
              <Input
                id="drv-title"
                value={editDriver?.title || ''}
                onChange={(e) => setEditDriver(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="drv-desc">Description</Label>
              <Textarea
                id="drv-desc"
                rows={3}
                value={editDriver?.description || ''}
                onChange={(e) => setEditDriver(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="drv-order">Display Order</Label>
                <Input
                  id="drv-order"
                  type="number"
                  value={editDriver?.displayOrder || 1}
                  onChange={(e) => setEditDriver(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="drv-year">Year</Label>
                <Input
                  id="drv-year"
                  type="number"
                  value={editDriver?.year || new Date().getFullYear()}
                  onChange={(e) => setEditDriver(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="drv-progress">Your assessment (%)</Label>
              <Input
                id="drv-progress"
                type="number"
                min={0}
                max={100}
                value={editDriver?.assessedProgressPercentage ?? 0}
                onChange={(e) => setEditDriver(prev => ({ ...prev, assessedProgressPercentage: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div>
              <Label htmlFor="drv-kpi">KPIs</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  id="drv-kpi"
                  placeholder="Add a KPI"
                  value={kpiInput}
                  onChange={(e) => setKpiInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKpi(); } }}
                />
                <Button type="button" variant="outline" onClick={addKpi}>Add</Button>
              </div>
              {editDriver?.kpis && editDriver.kpis.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {editDriver.kpis.map((kpi, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/30 px-2 py-0.5 text-[11px] font-medium"
                    >
                      {kpi}
                      <button
                        type="button"
                        onClick={() => setEditDriver(prev => ({
                          ...prev,
                          kpis: prev?.kpis?.filter((_, idx) => idx !== i),
                        }))}
                        aria-label={`Remove ${kpi}`}
                        className="rounded-full p-0.5 hover:bg-foreground/10"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="drv-notes">Notes</Label>
              <Textarea
                id="drv-notes"
                rows={2}
                value={editDriver?.notes || ''}
                onChange={(e) => setEditDriver(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            {/* Organisational toggle lives on the driver card itself so it's
                one click away and visible to EAs of TenantAdmins, not just
                admins. */}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TimeInvestedRow — small row in the per-driver "Time invested" footer.
// Shows two icon + minute-count pairs: work item time (ClipboardCheck) and
// meeting time (CalendarRange). When the driver isn't organisational, the row
// has no label. When it is, the label is "Self" plus either "Team" (the
// anonymous aggregate) or one row per named contributor for tenant admins.
// ---------------------------------------------------------------------------
interface TimeInvestedRowProps {
  label: string | null;
  data: { workItemMinutes: number; meetingMinutes: number };
  formatMinutes: (m: number) => string;
}

function TimeInvestedRow({ label, data, formatMinutes }: TimeInvestedRowProps) {
  return (
    <div className="flex items-center gap-4">
      {label !== null && (
        <p className="min-w-[40px] max-w-[120px] truncate text-xs font-semibold text-muted-foreground" title={label}>{label}</p>
      )}
      <div className="flex items-center gap-1">
        <ClipboardCheck className="h-3.5 w-3.5 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">{formatMinutes(data.workItemMinutes)}</p>
      </div>
      <div className="flex items-center gap-1">
        <CalendarRange className="h-3.5 w-3.5 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">{formatMinutes(data.meetingMinutes)}</p>
      </div>
    </div>
  );
}
